'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface BrandLogoProps {
  /** Size of the logo image in px */
  size?: number;
  /** Show brand name text next to logo */
  showText?: boolean;
  /** Show tagline below brand name */
  showTagline?: boolean;
  /** Wrap in a Link to /home */
  asLink?: boolean;
  /** Extra className on the wrapper */
  className?: string;
  /** Text color variant */
  textColor?: 'light' | 'dark';
  /** Enable hover glow animation */
  glow?: boolean;
}

export default function BrandLogo({
  size = 44,
  showText = true,
  showTagline = true,
  asLink = true,
  className = '',
  textColor = 'light',
  glow = true,
}: BrandLogoProps) {
  const nameColor = textColor === 'light' ? 'var(--himal-white, #F5F0E8)' : '#0B3C6D';
  const taglineColor = textColor === 'light' ? 'var(--gold-bright, #D4AF37)' : '#D4AF37';

  const inner = (
    <span
      className={`inline-flex items-center gap-2.5 ${glow ? 'group' : ''} ${className}`}
      style={{ textDecoration: 'none' }}
    >
      {/* Logo image */}
      <span
        className={`relative shrink-0 rounded-xl overflow-hidden transition-all duration-300 ${
          glow
            ? 'group-hover:shadow-[0_0_18px_4px_rgba(212,175,55,0.45)] group-hover:scale-105'
            : ''
        }`}
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden="true"
      >
        <Image
          src="/assets/images/file_00000000eb9881f8ae3689204bfbf837-1785479869379.png"
          alt="YatraNest Nepal Logo"
          width={size}
          height={size}
          style={{ objectFit: 'contain', width: size, height: size }}
          priority
        />
      </span>

      {/* Brand text */}
      {showText && (
        <span className="leading-tight flex flex-col">
          <span
            className="font-serif font-semibold leading-none"
            style={{
              color: nameColor,
              fontSize: size >= 44 ? '1.1rem' : size >= 36 ? '1rem' : '0.875rem',
            }}
          >
            YatraNest Nepal
          </span>
          {showTagline && (
            <span
              className="font-medium tracking-widest uppercase leading-none mt-0.5"
              style={{
                color: taglineColor,
                fontSize: '0.6rem',
              }}
            >
              Your Journey Begins Here
            </span>
          )}
        </span>
      )}
    </span>
  );

  if (asLink) {
    return (
      <Link href="/home" aria-label="YatraNest Nepal — Home" style={{ textDecoration: 'none' }}>
        {inner}
      </Link>
    );
  }

  return inner;
}
